import { createApi } from "@reduxjs/toolkit/query/react";
import {
  GetQuizAsManagerResponse as GetQuizAsManager,
  ManagerQuizEntity,
  GetQuizzesAsManagerParams,
  GetQuizzesAsManagerResponse as GetQuizsAsManager,
  QuizListEntities as Entities,
  CreateQuizPayload,
  CreateQuizResponse as CreateQuiz,
  CreatedQuizEntity,
  UpdateQuizPayload,
  UpdateQuizResponse,
  UpdatedQuizEntity,
  GetQuizComponentsParams,
  GetQuizComponentsResponse,
  QuizComponentsEntities,
  ParsedQuestion,
} from "@/types/canonical";
import {
  GetQuizzesAsManagerRequestedModelFields,
  ManagerQuizRequestedModelFields,
} from "./quizFields";
import { privateBaseQuery } from "../base/private/privateBaseQuery";

export interface GetQuizDetailParams {
  quizId: number;
}

export const quizApi = createApi({
  reducerPath: "quizApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["QuizDetail", "QuizList"],
  endpoints: (builder) => ({
    getQuizzesAsManager: builder.query<Entities, GetQuizzesAsManagerParams>({
      query: (params) => {
        return {
          url: "/quiz",
          method: "GET",
          params,
          headers: {
            "X-Requested-Fields":
              GetQuizzesAsManagerRequestedModelFields.join(","),
          },
        };
      },
      transformResponse: (response: GetQuizsAsManager) => response.entities,
      providesTags: ["QuizList"],
    }),

    getQuizAsManager: builder.query<ManagerQuizEntity, GetQuizDetailParams>({
      query: ({ quizId }) => {
        const fields = ManagerQuizRequestedModelFields;

        return {
          url: `/quiz/${quizId}`,
          method: "GET",
          headers: {
            "X-Requested-Fields": fields.join(","),
          },
        };
      },
      transformResponse: (response: GetQuizAsManager) => response.entity,
      providesTags: (result, error, { quizId }) => [
        { type: "QuizDetail", id: quizId },
      ],
    }),

    getQuizComponents: builder.query<QuizComponentsEntities, GetQuizComponentsParams>({
      query: ({ page = 1, query } = { page: 1 }) => ({
        url: "/quiz-components.json",
        method: "GET",
        params: { page, query },
      }),
      transformResponse: (response: GetQuizComponentsResponse) => response.entities,
    }),

    cloneQuiz: builder.mutation<{ id?: number }, number>({
      query: (quizId) => ({
        url: `/quiz/clone/${quizId}`,
        method: "POST",
      }),
      transformResponse: (response: { entity?: { id?: number } }) =>
        response?.entity ?? {},
      invalidatesTags: ["QuizList"],
    }),

    updateQuiz: builder.mutation<UpdatedQuizEntity, UpdateQuizPayload>({
      query: (payload) => ({
        url: `/quiz/${payload.id}`,
        method: "PUT",
        data: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      transformResponse: (response: UpdateQuizResponse) => response.entity,
      // Note: intentionally no invalidatesTags to avoid extra GET request after PUT
      // The response from PUT already contains updated data
    }),

    createQuiz: builder.mutation<CreatedQuizEntity, CreateQuizPayload>({
      query: (newQuiz) => ({
        url: `/quiz`,
        method: "POST",
        data: newQuiz,
      }),
      transformResponse: (response: CreateQuiz) => response.entity,
      invalidatesTags: ["QuizList", "QuizDetail"],
    }),

    createMultipleQuestions: builder.mutation<any, ParsedQuestion[]>({
      query: (questions) => ({
        url: "/multiple-questions",
        method: "POST",
        data: questions,
      }),
      transformResponse: (response) => response,
      invalidatesTags: ["QuizList"],
    }),

    // Обновление существующих вопросов (PUT /multiple-questions)
    updateMultipleQuestions: builder.mutation<any, ParsedQuestion[]>({
      query: (questions) => ({
        url: "/multiple-questions",
        method: "PUT",
        data: questions,
      }),
      transformResponse: (response) => response,
      invalidatesTags: ["QuizList", "QuizDetail"],
    }),

    deleteQuiz: builder.mutation<void, number>({
      query: (quizId) => ({
        url: `/quiz/${quizId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, quizId) => [
        { type: "QuizDetail", id: quizId },
        "QuizList",
      ],
    }),
  }),
});

export const {
  useGetQuizzesAsManagerQuery,
  useUpdateQuizMutation,
  useCreateQuizMutation,
  useGetQuizAsManagerQuery,
  useLazyGetQuizAsManagerQuery,
  useGetQuizComponentsQuery,
  useCloneQuizMutation,
  useCreateMultipleQuestionsMutation,
  useUpdateMultipleQuestionsMutation,
  useDeleteQuizMutation,
} = quizApi;
